<template>
  <hl-Modal dismisable @close="toggleNewInvoiceModal(false)">
    <ValidationObserver v-slot="{ invalid }" class="text-left">
      <div class="mb-3">
        <h3 class="text-center">Opprett {{ invoiceTypeMap[invoiceType] }}</h3>

        <InfoCard class="mt-4">
          For å sende ekstra husleiefaktura til din leietaker, fyller du ut info i feltene under. Skal leietaker ha
          tilsendt faktura så husk å merke “Ja” for dette. Etter du har klikket “Opprett” legges den i fakturalisten.
        </InfoCard>

        <div class="row mt-5 align-items-center">
          <div class="col">Utstedelsdato</div>
          <div class="col font-weight-bold">
            <hl-Date :date="newInvoice.creationDate"></hl-Date>
          </div>

          <div class="col">Send til leietaker?</div>
          <div class="col">
            <hl-Switch v-model="newInvoice.sendCopy" onState="Ja" offState="Nei" showState></hl-Switch>
          </div>
        </div>

        <div class="row mt-1 align-items-center">
          <div class="col">Fakturaperiode</div>
          <div class="col font-weight-bold">
            <ValidationProvider
              name="Fakturaperiode"
              :rules="validationRules.tenancy.payments.newInvoice.invoicePeriod"
              v-slot="{ errors }"
            >
              <hl-Input v-model="newInvoice.invoicePeriod" :error="errors[0]" />
            </ValidationProvider>
          </div>

          <div class="col">Forfallsdato</div>
          <div class="col font-weight-bold">
            <ValidationProvider
              name="Forfallsdato"
              :rules="validationRules.tenancy.payments.newInvoice.dueDate"
              v-slot="{ errors }"
            >
              <!-- Sudo Users & Pro users can backdate -->
              <hl-Datepicker
                v-if="isLandlordProfessional || isUserSudo"
                size="sm"
                :error="errors[0]"
                v-model="newInvoice.dueDate"
              ></hl-Datepicker>
              <!-- Others can't -->
              <hl-Datepicker
                v-else
                size="sm"
                :error="errors[0]"
                :disabled-dates="{ to: yesterday }"
                v-model="newInvoice.dueDate"
              ></hl-Datepicker>
            </ValidationProvider>
          </div>
        </div>
      </div>

      <div class="bg-light mt-5 pb-5 bg-absolute">
        <div class="table-responsive">
          <table class="ledger-table table">
            <thead>
              <tr>
                <th scope="col">Beskrivelse</th>
                <th scope="col">MVA</th>
                <th scope="col" style="width: 170px">Enhetspris eks. MVA</th>
              </tr>
            </thead>
            <tbody>
              <!-- New items -->
              <tr v-for="(row, rowIndex) in newInvoice.rows" :key="rowIndex">
                <td scope="row">
                  <ValidationProvider
                    name="Beskrivelse"
                    :rules="validationRules.tenancy.payments.newInvoice.rows.description"
                    v-slot="{ errors }"
                  >
                    <hl-Input
                      size="sm"
                      v-model="row.text"
                      placeholder="Produktbeskrivelse"
                      :error="errors[0]"
                      :disabled="row.disabled"
                    ></hl-Input>
                  </ValidationProvider>
                </td>
                <td>
                  <hl-Select
                    :hasMarginBottom="false"
                    size="sm"
                    v-model="row.vat"
                    :options="MVAOptions"
                    :disabled="row.disabled"
                  ></hl-Select>
                </td>
                <td style="width: 170px">
                  <ValidationProvider
                    name="Enhetspris"
                    :rules="validationRules.tenancy.payments.newInvoice.dueDate"
                    v-slot="{ errors }"
                  >
                    <hl-Input
                      type="number"
                      size="sm"
                      v-model="row.amount"
                      :error="errors[0]"
                      :disabled="row.disabled"
                    ></hl-Input>
                  </ValidationProvider>
                </td>

                <td>
                  <button v-if="!row.disabled" class="btn btn-sm remove-row">
                    <i @click="removeRow(rowIndex)" class="budicon-trash"></i>
                  </button>
                </td>
              </tr>

              <tr>
                <td colspan="4">
                  <div class="add-row" @click="addRow">Legg til fakturalinje</div>
                </td>
              </tr>

              <tr class="saldo">
                <td>Sum uten MVA</td>
                <td></td>
                <td></td>
                <td class="text-right">
                  <hl-Currency :amount="calculatedSumWithoutVAT"></hl-Currency>
                </td>
              </tr>

              <tr>
                <td>Sum MVA</td>
                <td></td>
                <td></td>
                <td class="text-right">
                  <hl-Currency :amount="calculatedSumVAT"></hl-Currency>
                </td>
              </tr>
            </tbody>
            <tbody>
              <tr>
                <th>Total å betale</th>
                <td></td>
                <td></td>
                <th class="text-right">
                  <hl-Currency :amount="totalSum"></hl-Currency>
                </th>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="my-4 text-center">
          <!-- @click="createOrder" -->
          <button
            class="btn btn-primary" :disabled="saving || invalid" @click="createOrder">
            <span>Opprett</span>
            <span v-if="saving" class="spinner-grow spinner-grow-sm ml-3" role="status" aria-hidden="true"></span>
          </button>

          <button class="btn btn-secondary ml-2" @click="toggleNewInvoiceModal(false)">Avbryt</button>
        </div>
      </div>
    </ValidationObserver>
  </hl-Modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import * as moment from 'moment';
import addValidationRules from '@/mixins/addValidationRules.js';
import canLandlordChargeVAT from '@/mixins/tenancy/canLandlordChargeVAT';
import Modal from '@modules/husleie-framework/src/components/modals/Modal.vue';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import Datepicker from '@modules/husleie-framework/src/components/forms/inputs/Datepicker.vue';
import Date from '@modules/husleie-framework/src/components/time/Date.vue';
import Switch from '@modules/husleie-framework/src/components/forms/inputs/switch/Switch.vue';
import Select from '@modules/husleie-framework/src/components/forms/inputs/select/Select.vue';
import Currency from '@modules/husleie-framework/src/components/currency/Currency.vue';
import InfoCard from '@/components/InfoCard.vue';

export default {
  components: {
    InfoCard,
    'hl-Modal': Modal,
    'hl-Date': Date,
    'hl-Input': Input,
    'hl-Switch': Switch,
    'hl-Datepicker': Datepicker,
    'hl-Select': Select,
    'hl-Currency': Currency,
  },

  mixins: [canLandlordChargeVAT, addValidationRules],

  props: {
    invoiceType: {
      type: String,
      required: true,
    },
    landlordId: [String, Number],
    tenancyId: [String, Number],
  },

  data() {
    return {
      saving: false,
      newInvoice: {
        creationDate: moment().format('YYYY-MM-DD'),
        invoicePeriod: '',
        dueDate: '',
        sendCopy: true,
        rows: [],
      },

      invoiceTypeMap: {
        additional: 'Tilleggsfaktura',
        rent: 'Husleiefaktura',
      },
    };
  },

  computed: {
    ...mapGetters(['getLandlordProfileMeta', 'isUserSudo']),
    ...mapGetters('tenancy', ['getTenancy']),

    yesterday() {
      return moment().subtract(1, 'day').toDate();
    },

    orderToBeCreated() {
      let typeOf = '';
      if (this.invoiceType === 'additional') {
        typeOf = 'otherInvoice';
      }

      if (this.invoiceType === 'rent') {
        typeOf = 'rentInvoice';
      }

      return {
        dueDate: this.newInvoice.dueDate,
        period: this.newInvoice.invoicePeriod,
        rows: this.newInvoice.rows,
        sendCopy: this.newInvoice.sendCopy,
        typeOf,
      };
    },

    landlord() {
      return this.getLandlordProfileMeta(this.landlordId);
    },

    isLandlordProfessional() {
      return this.landlord.type === 'professional';
    },

    calculatedSumWithoutVAT() {
      const sum = this.newInvoice.rows.reduce((acc, row) => {
        return acc + parseFloat(row.amount || 0);
      }, 0);
      return sum;
    },

    calculatedSumVAT() {
      const sum = this.newInvoice.rows.reduce((acc, row) => {
        return acc + (row.amount * row.vat) / 100;
      }, 0);
      return sum;
    },

    totalSum() {
      if (this.calculatedSumWithoutVAT == null || this.calculatedSumVAT == null) return;
      return this.calculatedSumWithoutVAT + this.calculatedSumVAT;
    },
  },

  methods: {
    ...mapActions('tenancy', ['toggleNewInvoiceModal', 'postTenancyAccountingOrder']),

    addRow() {
      this.newInvoice.rows.push({
        text: '',
        vat: 0,
        amount: null,
      });
    },

    removeRow(rowIndex) {
      this.newInvoice.rows.splice(rowIndex, 1);
    },

    addDefaultItems() {
      const rows = this.newInvoice.rows;
      const baseRentPrice = this.getTenancy.rent.rent;
      const baseRentVat = this.getTenancy.rent.vat;

      rows.push({
        text: 'Husleie',
        vat: baseRentVat,
        amount: baseRentPrice,
        categoryId: 1,
        disabled: false,
      });

      const subProducts = this.getTenancy.subProducts;
      if (subProducts == null) return;

      for (const item of subProducts) {
        rows.push({
          id: item.id,
          text: item.name,
          vat: item.VATPercentage ? item.vat : 0,
          amount: item.price,
          categoryId: 2,
          disabled: false,
        });
      }
    },

    async createOrder() {
      this.saving = true;
      try {
        await this.postTenancyAccountingOrder({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          data: this.orderToBeCreated,
        });
        this.toggleNewInvoiceModal(false);
        this.$emit('order-created');
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke lagre ordre.', 'error');
      }
      this.saving = false;
    },
  },

  mounted() {
    if (this.invoiceType === 'rent') {
      this.addDefaultItems();
    } else {
      this.addRow();
    }
  },

  beforeDestroy() {
    // ensure that we destroy an unsaved invoice
    this.newInvoice = {};
  },
};
</script>

<style scoped lang="scss">
.bg-absolute {
  margin-left: -3rem;
  margin-bottom: -3rem;
  margin-top: -3rem;
  width: calc(100% + 6rem);
  padding: 3rem;
}

.add-row {
  //background: $gray-100;
  width: 100%;
  padding: 1rem;
  background: $gray-100;
  text-align: center;
  border: 1px solid $gray-300;
  border-radius: $border-radius-medium;
  color: $gray-800;
  box-shadow: $box-shadow-1;
  cursor: pointer;
}

.remove-row {
  background: white;
  border: 1px solid $gray-400;
  height: 31px;

  i {
    font-size: 1rem !important;
  }
}

hr {
  border-bottom: 1px solid $gray-500;
  margin: 0.4rem 15px;
}
</style>
